import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import SafeIcon from '../../common/SafeIcon';
import { FiAlertTriangle, FiCheckCircle, FiExternalLink, FiSettings } from 'react-icons/fi';


const ConnectionStatus = () => {
  const { settings } = useSettings();
  
  const hasThoughtlyKey = !!settings.thoughtlyApiKey;
  const hasTeamId = !!settings.thoughtlyTeamId;
  const isConnected = hasThoughtlyKey && hasTeamId;

  // Mock usage data - in real app this would come from Thoughtly API
  const usageStats = {
    callsThisMonth: 147,
    callsLimit: 1000,
    remainingBalance: '$45.50'
  };

  const usagePercentage = (usageStats.callsThisMonth / usageStats.callsLimit) * 100;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Thoughtly Integration</h3>
        <div className="flex items-center gap-3">
          <a
            href="https://app.thoughtly.co"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm font-medium"
          >
            Thoughtly Dashboard <SafeIcon icon={FiExternalLink} className="text-xs" />
          </a>
          <Link
            to="/settings"
            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <SafeIcon icon={FiSettings} className="text-xs" />
            Configure
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Connection Status */}
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full mr-3 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {isConnected ? 'Connected' : 'Not Connected'}
            </p>
            <p className="text-xs text-gray-500">
              {isConnected ? 'API credentials configured' : 'API credentials required'}
            </p>
          </div>
          <SafeIcon 
            icon={isConnected ? FiCheckCircle : FiAlertTriangle} 
            className={`ml-auto text-lg ${isConnected ? 'text-green-500' : 'text-red-500'}`} 
          />
        </div>

        {/* Usage Stats */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-900">Monthly Usage</p>
            <p className="text-sm text-gray-600">{usageStats.callsThisMonth}/{usageStats.callsLimit}</p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                usagePercentage > 80 ? 'bg-red-500' : 
                usagePercentage > 60 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {usageStats.callsLimit - usageStats.callsThisMonth} calls remaining
          </p>
        </div>

        {/* Account Balance */}
        <div>
          <p className="text-sm font-medium text-gray-900 mb-1">Account Balance</p>
          <p className="text-2xl font-bold text-gray-900">{usageStats.remainingBalance}</p>
          <p className="text-xs text-gray-500">Available credits</p>
        </div>
      </div>

      {/* Warnings */}
      {!isConnected && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <SafeIcon icon={FiAlertTriangle} className="text-red-500 mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-red-800">Thoughtly API Not Connected</p>
              <p className="text-sm text-red-700">
                Configure your Thoughtly API key and team ID in settings to enable AI calling functionality.
              </p>
            </div>
          </div>
        </div>
      )}

      {usagePercentage > 80 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Usage Warning</p>
              <p className="text-sm text-yellow-700">
                You've used {Math.round(usagePercentage)}% of your monthly call limit. Consider upgrading your plan.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;